import { build } from 'search-params';
import { Reservation, GetReservationsResponse } from 'models/reservations';
import { RESERVATION_STATUSES } from 'interfaces/reservations';
import $http from 'services/api/instance';

const PAGE_SIZE = 500;

/**
 * Only request the fields we actually need for the guests page.
 * This dramatically reduces response payload size — we skip all the
 * formatted price strings, payment details, calendar data, etc.
 */
const GUEST_PAGE_FIELDS = [
  'id',
  'code',
  'channel',
  'status',
  'nights',
  'checkin_time',
  'start_date',
  'checkout_time',
  'end_date',
  'total_price',
  'subtotal',
  'base_price',
  'currency',
  'createdAt',
  'guest',
  'listing',
].join(',');

export interface FetchReservationsOpts {
  /** ISO date string for checkin_time >= */
  dateFrom?: string;
  /** ISO date string for checkin_time <= */
  dateTo?: string;
  /** Check if the caller has been cancelled (e.g. React unmount) */
  isCancelled?: () => boolean;
}

/**
 * Fetch reservations from the API, paginating through all pages.
 * Accepts optional date range to filter server-side (checkin_time).
 * Uses `fields` param to only request data needed by the guests page.
 */
export async function fetchReservations(opts: FetchReservationsOpts = {}): Promise<Reservation[]> {
  const { dateFrom, dateTo, isCancelled = () => false } = opts;
  const all: Reservation[] = [];
  let offset = 0;
  let totalCount = Infinity;

  while (offset < totalCount) {
    if (isCancelled()) return all;

    const params: Record<string, any> = {
      offset,
      limit: PAGE_SIZE,
      sortBy: 'checkin_time',
      sortOrder: 'desc',
      fields: GUEST_PAGE_FIELDS,
      status: RESERVATION_STATUSES.ACCEPTED,
    };

    if (dateFrom) {
      params['checkin_time[gte]'] = dateFrom;
    }
    if (dateTo) {
      params['checkin_time[lte]'] = dateTo;
    }

    const query = build(params);
    const res = await $http.get(`/reservations?${query}`);
    const { data } = res as { data: GetReservationsResponse };

    if (!data?.content?.reservations) {
      console.warn('Unexpected API response shape at offset', offset, data);
      break;
    }

    const { reservations: batch } = data.content;
    totalCount = data._metadata?.totalCount ?? 0;

    all.push(...batch);
    offset += PAGE_SIZE;

    if (batch.length === 0) break;
  }

  return all;
}
